// useDocumentTitle.js
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Custom hook for setting and managing document title
 *
 * @param {string|object} title - Title key or object with title and params
 * @param {object} [options] - Additional options
 * @param {boolean} [options.restoreOnUnmount=true] - Whether to restore previous title on unmount
 * @param {string} [options.suffix] - Optional suffix to append to title
 */
const useDocumentTitle = (title, options = {}) => {
  const { t } = useTranslation();
  const { restoreOnUnmount = true, suffix = "appTitle" } = options;
  const previousTitle = useRef(document.title);

  const translatedTitle = useMemo(() => {
    if (!title) return null;
    return typeof title === "string" ? t(title) : t(title.key, title.params);
  }, [title, t]);

  useEffect(() => {
    if (!translatedTitle) return;
    document.title = suffix ? `${translatedTitle} - ${t(suffix)}` : translatedTitle;

    return () => {
      if (restoreOnUnmount) document.title = previousTitle.current;
    };
  }, [translatedTitle, t, suffix, restoreOnUnmount]);
};

export default useDocumentTitle;
